import React, { useEffect, useMemo, useState } from "react";

import { Box, Card, CircularProgress } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import Stepper from "@remar/shared/dist/components/Stepper";
import { SignupStepsLabels } from "@remar/shared/dist/constants";
import useStateWithCallback from "@remar/shared/dist/hooks/useStateWithCallback";
import ContentLoader from "@remar/shared/dist/layouts/TableContentLayout/components/ContentLoader";
import { Country, IErrorResponseData, Integrations } from "@remar/shared/dist/models";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Form, Formik } from "formik";
import { isEmpty } from "lodash";

import { useHistory, useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "store";
import {
	fetchCountries,
	getFullState as getFullAuthState,
	getInvitationDetails,
	signInWithTokenFromResponse,
	studentSignUpInvitation
} from "store/features/Auth/authSlice";
import { USA_COUNTRY_ID } from "store/features/Auth/constants";
import { getFullState as getFullThemeState } from "store/features/Theme/theme.slice";

import logo from "assets/images/logo.svg";

import { routes } from "core/constants";

import Summary from "./Summary";
import { accountInitialValue, paymentInitValue, shippingInitValue } from "./types";

import SignUpLayout from "../SignUpLayout";
import FormActions from "../components/FormActions";
import { AccountFormDetails, PaymentForm, ShippingForm } from "../components/Forms";
import SignUpFollowUpScreen from "../components/SignUpFollowUpScreen";

import { AccountSchema, PaymentSchema, ShippingSchema } from "../components/schemas";

import { CardsContainer, useStyles } from "../components/styles";
import { invitationSignUpFormKeys, validateFormValues } from "../components/utils";

export enum Steps {
	ACCOUNT,
	SHIPPING,
	PAYMENT,
	SUCCESS,
	ERROR
}
const steps = [
	{ label: SignupStepsLabels.account },
	{ label: SignupStepsLabels.shipping },
	{ label: SignupStepsLabels.payment }
];
const Logo: React.ReactNode = () => {
	const { logoImageUrl, isLoading } = useAppSelector(getFullThemeState);
	return (
		<Box display={"flex"} justifyContent={"center"}>
			{isLoading ? (
				<ContentLoader size={"5rem"} height={50} />
			) : (
				<img alt="institution logo" src={logoImageUrl || logo} height={50} />
			)}
		</Box>
	);
};

const InvitationSingUp = () => {
	const { invitationId } = useParams<{ invitationId: string }>();
	const { isLoading, invitationDetails, invitationDetailsLoading, invitationError, selectedCountryId, token, user } =
		useAppSelector(getFullAuthState);
	const classes = useStyles();
	const dispatch = useAppDispatch();
	const history = useHistory();
	const elements = useElements();
	const stripe = useStripe();
	const [activeStep, setActiveStep] = useStateWithCallback(Steps.ACCOUNT);
	const [shouldShowTryAgainBtn, setShouldShowTryAgainBtn] = useState(true);
	const [country, setCountry] = useState<Country>();
	const [physicalBook, setPhysicalBook] = useState<boolean>(false);
	const [skipPaymentStep, setSkipPaymentStep] = useState<boolean>(false);

	const initialValues = useMemo(() => {
		if (invitationDetails?.subscriptionData) {
			const { subscriptionData } = invitationDetails;
			const subTypeEIDItems = subscriptionData?.subTypeEIDItems?.find(
				item => item.integrationId === Integrations.StripeIntegrationId && item.parentId === null
			);
			const isPhysicalBook = subTypeEIDItems?.children?.some(
				book => book.integrationId !== Integrations.DigitalAssetIntegrationId
			);
			setPhysicalBook(isPhysicalBook);
		}

		const updatedInitialValue = {
			...accountInitialValue,
			email: invitationDetails?.user.email,
			firstName: invitationDetails?.user.firstName,
			lastName: invitationDetails?.user.lastName
		};
		return {
			...updatedInitialValue,
			...shippingInitValue,
			...paymentInitValue
		};
	}, [invitationDetails]);
	const schema = useMemo(() => {
		if (activeStep === Steps.ACCOUNT) return AccountSchema;
		if (activeStep === Steps.SHIPPING) return ShippingSchema;
		if (activeStep === Steps.PAYMENT) return PaymentSchema;
	}, [activeStep]);

	useEffect(() => {
		if (!invitationDetails) return;
		let skipPayment = invitationDetails.invitedBy.coverStudentShippingCharges || !physicalBook;
		if (country && physicalBook && !invitationDetails.invitedBy.coverStudentShippingCharges) {
			skipPayment = country.freeShippingForInstitution;
		}
		setSkipPaymentStep(skipPayment);
		if (skipPayment) {
			steps.some(step => step.label == SignupStepsLabels.payment) && steps.pop();
		} else if (!steps.some(step => step.label == SignupStepsLabels.payment)) {
			steps.push({ label: SignupStepsLabels.payment });
		}
	}, [dispatch, country, physicalBook]);

	useEffect(() => {
		if (invitationId) {
			dispatch(
				getInvitationDetails({
					inviteCode: invitationId
				})
			);
		}
	}, [dispatch, history, invitationId]);
	useEffect(() => {
		dispatch(fetchCountries(0));
	}, [dispatch]);

	useEffect(() => {
		if (!invitationError) return;
		setShouldShowTryAgainBtn(false);
		setActiveStep(Steps.ERROR);
	}, [invitationError]);

	useEffect(() => {
		if (user && token) {
			history.replace(routes.signIn.getPath());
		}
	}, [user, token]);

	const handleNext = (values, validateForm = () => {}) => {
		if (activeStep === Steps.SHIPPING && skipPaymentStep) {
			handleSubmit(values);
		} else if (activeStep === Steps.PAYMENT) {
			handleSubmit(values);
		} else {
			setActiveStep(
				prevState => prevState + 1,
				() => validateForm()
			);
		}
	};
	const handleSubmit = values => {
		dispatch(
			studentSignUpInvitation({
				CardElement,
				elements,
				stripe,
				skipPayment: skipPaymentStep,
				values: { invitationId, ...values }
			})
		).then(response => {
			if (response.error) {
				setActiveStep(Steps.ERROR);
			} else {
				setActiveStep(Steps.SUCCESS);
				dispatch(signInWithTokenFromResponse(response.payload));
			}
		});
	};

	const handleClickFollowUp = () => {
		// try again clicked
		setActiveStep(!skipPaymentStep ? Steps.PAYMENT : Steps.SHIPPING);
	};

	return invitationDetailsLoading ? (
		<Box display="flex" alignItems="center" justifyContent="center" height={450}>
			<CircularProgress size="7rem" color="primary" thickness={5} variant="indeterminate" />
		</Box>
	) : (
		<SignUpLayout logo={Logo}>
			<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" mb={5} mt={5}>
				<CardsContainer $activeStep={"column"} display="flex" justifyContent="center">
					<Formik
						initialValues={initialValues}
						validationSchema={schema}
						validateOnChange
						onSubmit={values => {
							handleNext(values);
						}}
					>
						{({
							isValid,
							values,
							setErrors,
							setFieldValue,
							setTouched,
							setFieldTouched,
							handleChange,
							validateForm,
							dirty,
							errors,
							resetForm
						}) => {
							let valid = isValid && isEmpty(errors) && dirty;
							if (!dirty && activeStep !== Steps.SUCCESS && activeStep !== Steps.ERROR) {
								valid = validateFormValues(invitationSignUpFormKeys[activeStep], values);
							}
							switch (activeStep) {
								case Steps.ACCOUNT:
								case Steps.SHIPPING:
								case Steps.PAYMENT: {
									return (
										<>
											<Card className={`${classes.card} ${classes.baseFlex}`}>
												<Box>
													<Box px={6} pt={2} className={classes.stepperForm}>
														{(activeStep === Steps.SHIPPING || activeStep === Steps.PAYMENT) &&
															selectedCountryId &&
															selectedCountryId !== USA_COUNTRY_ID && (
																<Alert style={{ margin: "16px 0" }} severity="info">
																	Payment for custom fees may be required at the time of delivery.
																</Alert>
															)}
														<Stepper activeStep={activeStep} steps={steps} />
														<Form>
															{activeStep === Steps.ACCOUNT && (
																<AccountFormDetails showPasswordOption={true} disabledField />
															)}
															{activeStep === Steps.SHIPPING && (
																<ShippingForm
																	setCountry={setCountry}
																	country={country}
																	setTouchedField={setFieldTouched}
																	handleChanged={handleChange}
																/>
															)}
															{activeStep === Steps.PAYMENT && (
																<PaymentForm
																	setFieldValue={setFieldValue}
																	setTouched={setTouched}
																	termValue={values.terms}
																	showTerms={true}
																	setTouchedField={setFieldTouched}
																	handleChanged={handleChange}
																/>
															)}
															<FormActions
																back={() => {
																	if (activeStep === Steps.PAYMENT) {
																		setFieldValue("validCardDetails", false);
																		setFieldTouched("validCardDetails", false, false);
																	}
																	setErrors({});
																	setActiveStep(
																		prevState => (prevState > Steps.ACCOUNT ? prevState - 1 : Steps.ACCOUNT),
																		() => {
																			validateForm();
																		}
																	);
																}}
																next={() => {
																	resetForm({ values });
																	handleNext(values, validateForm);
																}}
																valid={valid}
																loading={isLoading}
																step={activeStep}
																showSignIn={false}
																lastStep={skipPaymentStep ? Steps.SHIPPING : Steps.PAYMENT}
															/>
														</Form>
													</Box>
												</Box>
											</Card>
											<Summary
												invitedBy={invitationDetails?.invitedBy}
												subscriptionData={invitationDetails?.subscriptionData}
												country={country}
												skipPayment={skipPaymentStep}
											/>
										</>
									);
								}
								case Steps.SUCCESS:
								case Steps.ERROR:
									return (
										<SignUpFollowUpScreen
											success={activeStep === Steps.SUCCESS}
											error={activeStep === Steps.ERROR}
											invitationError={invitationError as IErrorResponseData}
											showTryAgainButton={shouldShowTryAgainBtn && activeStep === Steps.ERROR}
											onClick={handleClickFollowUp}
										/>
									);
								default:
									return;
							}
						}}
					</Formik>
				</CardsContainer>
			</Box>
		</SignUpLayout>
	);
};

export default InvitationSingUp;
